import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, FolderKanban, Building2, Calendar, CheckCircle2, Circle, Clock, Loader2, AlertCircle, User, Target, AlertTriangle, ListTodo } from 'lucide-react';
import { projectsAPI } from '../services/api';

// Status badge component for tickets
function StatusBadge({ status, closedFlag }) {
  const statusName = status?.name || 'Open';
  const lowerStatus = statusName.toLowerCase();

  // Determine color based on status
  let colorClass = 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300'; // Default: Open/Pending

  if (closedFlag || lowerStatus.includes('closed') || lowerStatus.includes('complete') || lowerStatus.includes('done')) {
    colorClass = 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300';
  } else if (lowerStatus.includes('progress') || lowerStatus.includes('working') || lowerStatus.includes('active')) {
    colorClass = 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300';
  } else if (lowerStatus.includes('hold') || lowerStatus.includes('wait') || lowerStatus.includes('blocked')) {
    colorClass = 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300';
  } else if (lowerStatus.includes('open') || lowerStatus.includes('new')) {
    colorClass = 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300';
  }

  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${colorClass}`}>
      {closedFlag ? 'Completed' : statusName}
    </span>
  );
}

// Priority badge for issues
function PriorityBadge({ priority }) {
  const priorityName = priority?.name || 'Normal';
  const isHigh = priorityName.toLowerCase().includes('high') || priorityName.toLowerCase().includes('critical');
  const isLow = priorityName.toLowerCase().includes('low');

  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
      isHigh
        ? 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300'
        : isLow
        ? 'bg-neutral-100 text-neutral-600 dark:bg-neutral-700 dark:text-neutral-400'
        : 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300'
    }`}>
      {priorityName}
    </span>
  );
}

export default function ProjectDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('phases');

  const { data, isLoading, error } = useQuery({
    queryKey: ['project', id],
    queryFn: () => projectsAPI.get(id),
    enabled: !!id,
  });

  // Fetch tickets when tab is active
  const { data: ticketsData, isLoading: ticketsLoading } = useQuery({
    queryKey: ['project', id, 'tickets'],
    queryFn: () => projectsAPI.getTickets(id),
    enabled: !!id && activeTab === 'tickets',
  });

  // Fetch issues when tab is active
  const { data: issuesData, isLoading: issuesLoading } = useQuery({
    queryKey: ['project', id, 'issues'],
    queryFn: () => projectsAPI.getIssues(id),
    enabled: !!id && activeTab === 'issues',
  });

  const project = data?.data?.project;
  const phases = data?.data?.phases || [];
  const tickets = ticketsData?.data?.tickets || [];
  const issues = issuesData?.data?.issues || [];

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const BackButton = () => (
    <button
      onClick={() => {
        const returnUrl = sessionStorage.getItem('projectsReturnUrl') || '/projects';
        sessionStorage.removeItem('projectsReturnUrl');
        navigate(returnUrl, { state: { refresh: Date.now() } });
      }}
      className="flex items-center gap-2 text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-white mb-6"
    >
      <ArrowLeft className="w-4 h-4" />
      Back to Projects
    </button>
  );

  if (isLoading) {
    return (
      <div className="animate-fade-in">
        <BackButton />
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin text-primary-500 mx-auto mb-4" />
            <p className="text-neutral-500 dark:text-neutral-400">Loading project...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="animate-fade-in">
        <BackButton />
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-6 text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-red-900 dark:text-red-200 mb-2">Project Not Found</h2>
          <p className="text-red-700 dark:text-red-300">
            {error?.response?.data?.error || 'This project could not be loaded.'}
          </p>
        </div>
      </div>
    );
  }

  const isClosed = project.closedFlag;
  const percentComplete = project.percentComplete || 0;

  return (
    <div className="animate-fade-in max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <BackButton />
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">{project.name}</h1>
            <p className="text-neutral-500 dark:text-neutral-400">Project #{project.id}</p>
          </div>
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${
            isClosed
              ? 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300'
              : 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300'
          }`}>
            {project.status?.name || (isClosed ? 'Completed' : 'Active')}
          </span>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-6 mb-6">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">Progress</h2>
          <span className="text-2xl font-bold text-primary-600 dark:text-primary-400">{percentComplete}%</span>
        </div>
        <div className="h-4 bg-neutral-200 dark:bg-neutral-700 rounded-full overflow-hidden">
          <div
            className="h-full bg-primary-500 transition-all duration-500"
            style={{ width: `${percentComplete}%` }}
          />
        </div>
      </div>

      {/* Project Info */}
      <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-6 mb-6">
        <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">Project Details</h2>
        <div className="grid sm:grid-cols-2 gap-6">
          <div className="flex items-start gap-3">
            <Building2 className="w-5 h-5 text-neutral-400 mt-0.5" />
            <div>
              <div className="text-sm text-neutral-500 dark:text-neutral-400">Company</div>
              <div className="font-medium text-neutral-900 dark:text-white">{project.company?.name || 'N/A'}</div>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <User className="w-5 h-5 text-neutral-400 mt-0.5" />
            <div>
              <div className="text-sm text-neutral-500 dark:text-neutral-400">Project Manager</div>
              <div className="font-medium text-neutral-900 dark:text-white">
                {project.manager?.name || 'Unassigned'}
              </div>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Calendar className="w-5 h-5 text-neutral-400 mt-0.5" />
            <div>
              <div className="text-sm text-neutral-500 dark:text-neutral-400">Start Date</div>
              <div className="font-medium text-neutral-900 dark:text-white">{formatDate(project.estimatedStart)}</div>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Target className="w-5 h-5 text-neutral-400 mt-0.5" />
            <div>
              <div className="text-sm text-neutral-500 dark:text-neutral-400">Target End Date</div>
              <div className="font-medium text-neutral-900 dark:text-white">{formatDate(project.estimatedEnd)}</div>
            </div>
          </div>
          {project.actualStart && (
            <div className="flex items-start gap-3">
              <Clock className="w-5 h-5 text-neutral-400 mt-0.5" />
              <div>
                <div className="text-sm text-neutral-500 dark:text-neutral-400">Actual Start</div>
                <div className="font-medium text-neutral-900 dark:text-white">{formatDate(project.actualStart)}</div>
              </div>
            </div>
          )}
          {project.actualEnd && (
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-neutral-400 mt-0.5" />
              <div>
                <div className="text-sm text-neutral-500 dark:text-neutral-400">Actual End</div>
                <div className="font-medium text-neutral-900 dark:text-white">{formatDate(project.actualEnd)}</div>
              </div>
            </div>
          )}
        </div>

        {project.description && (
          <div className="mt-6 pt-6 border-t border-neutral-200 dark:border-neutral-700">
            <div className="text-sm text-neutral-500 dark:text-neutral-400 mb-2">Description</div>
            <p className="text-neutral-700 dark:text-neutral-300 whitespace-pre-wrap">{project.description}</p>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 overflow-hidden">
        {/* Tab Navigation */}
        <div className="border-b border-neutral-200 dark:border-neutral-700">
          <nav className="flex -mb-px">
            <button
              onClick={() => setActiveTab('phases')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'phases'
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <FolderKanban className="w-4 h-4" />
                Phases ({phases.length})
              </div>
            </button>
            <button
              onClick={() => setActiveTab('tickets')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'tickets'
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <ListTodo className="w-4 h-4" />
                Tickets {tickets.length > 0 && `(${tickets.length})`}
              </div>
            </button>
            <button
              onClick={() => setActiveTab('issues')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'issues'
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                Issues {issues.length > 0 && `(${issues.length})`}
              </div>
            </button>
          </nav>
        </div>

        {/* Phases Tab */}
        {activeTab === 'phases' && (
          <>
            {phases.length === 0 ? (
              <div className="p-8 text-center text-neutral-500 dark:text-neutral-400">
                <FolderKanban className="w-12 h-12 mx-auto mb-3 text-neutral-300 dark:text-neutral-600" />
                <p>No phases defined for this project</p>
              </div>
            ) : (
              <div className="divide-y divide-neutral-200 dark:divide-neutral-700">
                {phases.map((phase, index) => {
                  const phaseComplete = phase.actualEnd || phase.closedFlag;
                  return (
                    <div key={phase.id} className="p-4 flex items-start gap-4">
                      <div className="flex-shrink-0 mt-1">
                        {phaseComplete ? (
                          <CheckCircle2 className="w-5 h-5 text-green-500" />
                        ) : (
                          <Circle className="w-5 h-5 text-neutral-300 dark:text-neutral-600" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-neutral-400 dark:text-neutral-500">#{index + 1}</span>
                          <h3 className={`font-medium ${
                            phaseComplete
                              ? 'text-neutral-500 dark:text-neutral-400 line-through'
                              : 'text-neutral-900 dark:text-white'
                          }`}>
                            {phase.description || `Phase ${index + 1}`}
                          </h3>
                        </div>
                        <div className="flex items-center gap-4 mt-1 text-sm text-neutral-500 dark:text-neutral-400">
                          {phase.scheduledStart && (
                            <span>Start: {formatDate(phase.scheduledStart)}</span>
                          )}
                          {phase.scheduledEnd && (
                            <span>End: {formatDate(phase.scheduledEnd)}</span>
                          )}
                        </div>
                        {phase.notes && (
                          <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-400">{phase.notes}</p>
                        )}
                      </div>
                      {phase.wbsCode && (
                        <div className="flex-shrink-0 text-xs font-mono text-neutral-400 dark:text-neutral-500 bg-neutral-100 dark:bg-neutral-700 px-2 py-1 rounded">
                          {phase.wbsCode}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* Tickets Tab */}
        {activeTab === 'tickets' && (
          <>
            {ticketsLoading ? (
              <div className="p-8 text-center">
                <Loader2 className="w-8 h-8 animate-spin text-primary-500 mx-auto mb-3" />
                <p className="text-neutral-500 dark:text-neutral-400">Loading tickets...</p>
              </div>
            ) : tickets.length === 0 ? (
              <div className="p-8 text-center text-neutral-500 dark:text-neutral-400">
                <ListTodo className="w-12 h-12 mx-auto mb-3 text-neutral-300 dark:text-neutral-600" />
                <p>No tickets for this project</p>
              </div>
            ) : (
              <div className="divide-y divide-neutral-200 dark:divide-neutral-700">
                {tickets.map((ticket) => (
                  <div key={ticket.id} className="p-4 hover:bg-neutral-50 dark:hover:bg-neutral-700/50 transition-colors">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm text-neutral-400 dark:text-neutral-500">#{ticket.id}</span>
                          <StatusBadge status={ticket.status} closedFlag={ticket.closedFlag} />
                          {ticket.phase && (
                            <span className="text-xs text-neutral-500 dark:text-neutral-400 bg-neutral-100 dark:bg-neutral-700 px-2 py-0.5 rounded">
                              {ticket.phase.name || ticket.phase.description || (ticket.wbsCode ? `WBS ${ticket.wbsCode}` : `Phase ${ticket.phase.id}`)}
                            </span>
                          )}
                        </div>
                        <h4 className="font-medium text-neutral-900 dark:text-white">
                          {ticket.summary}
                        </h4>
                        {ticket.resources && (
                          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
                            Assigned: {ticket.resources}
                          </p>
                        )}
                        <div className="flex items-center gap-4 mt-2 text-xs text-neutral-500 dark:text-neutral-400">
                          {ticket.budgetHours && (
                            <span>Budget: {ticket.budgetHours}h</span>
                          )}
                          {ticket.actualHours > 0 && (
                            <span>Actual: {ticket.actualHours}h</span>
                          )}
                          {ticket.scheduledStart && (
                            <span>Start: {formatDate(ticket.scheduledStart)}</span>
                          )}
                          {ticket.scheduledEnd && (
                            <span>Due: {formatDate(ticket.scheduledEnd)}</span>
                          )}
                        </div>
                      </div>
                      {ticket.percentComplete !== undefined && (
                        <div className="flex-shrink-0 text-right">
                          <span className="text-lg font-semibold text-primary-600 dark:text-primary-400">
                            {ticket.percentComplete}%
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Issues Tab */}
        {activeTab === 'issues' && (
          <>
            {issuesLoading ? (
              <div className="p-8 text-center">
                <Loader2 className="w-8 h-8 animate-spin text-primary-500 mx-auto mb-3" />
                <p className="text-neutral-500 dark:text-neutral-400">Loading issues...</p>
              </div>
            ) : issues.length === 0 ? (
              <div className="p-8 text-center text-neutral-500 dark:text-neutral-400">
                <AlertTriangle className="w-12 h-12 mx-auto mb-3 text-neutral-300 dark:text-neutral-600" />
                <p>No issues reported for this project</p>
              </div>
            ) : (
              <div className="divide-y divide-neutral-200 dark:divide-neutral-700">
                {issues.map((issue) => (
                  <div key={issue.id} className="p-4 hover:bg-neutral-50 dark:hover:bg-neutral-700/50 transition-colors">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm text-neutral-400 dark:text-neutral-500">#{issue.id}</span>
                          <PriorityBadge priority={issue.priority} />
                          {issue.status && (
                            <StatusBadge status={issue.status} closedFlag={issue.resolvedFlag} />
                          )}
                        </div>
                        <h4 className="font-medium text-neutral-900 dark:text-white">
                          {issue.summary || issue.description}
                        </h4>
                        {issue.member && (
                          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
                            Assigned: {issue.member.name || issue.member.identifier}
                          </p>
                        )}
                        <div className="flex items-center gap-4 mt-2 text-xs text-neutral-500 dark:text-neutral-400">
                          {issue.dateEntered && (
                            <span>Reported: {formatDate(issue.dateEntered)}</span>
                          )}
                          {issue.dateResolved && (
                            <span className="text-green-600 dark:text-green-400">
                              Resolved: {formatDate(issue.dateResolved)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
